import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { QueryParams, QueryResult } from 'src/app/modules/core/store/models/query.model';
import { Collaborator } from '../../management/store/content-share';
import { Map, MapLayerView, MapLink } from 'src/app/modules/cop/store/cop/models';
import { ExportTemplate } from '../../management/store/datasets/dataset.model';
import { Dataset } from '../../core/store/models/dataset.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class MapApiService {
  constructor(private http: HttpService) {}

  public createMap(Map: Map): Observable<Map> {
    const url = `${apiBaseUrl}/v1/meta/maps/`;

    return this.http.post(url, Map);
  }

  public getMap(mapUid: string): Observable<Map> {
    const url = `${apiBaseUrl}/v1/meta/maps/${mapUid}`;

    return this.http.get<Map>(url);
  }

  public getMaps(params: QueryParams): Observable<QueryResult<Map>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/meta/maps?${queryString}`;

    return this.http.get<QueryResult<Map>>(url);
  }

  public deleteMap(Map: Map): Observable<void> {
    const url = `${apiBaseUrl}/v1/meta/maps/${Map.uId}`;

    return this.http.delete(url);
  }

  public updateMap(Map: Map): Observable<Map> {
    const url = `${apiBaseUrl}/v1/meta/maps/${Map.uId}`;

    return this.http.put(url, Map);
  }

  public shareMap(mapUId: string, collaborators: Collaborator[]): Observable<void> {
    const url = `${apiBaseUrl}/v1/meta/maps/${mapUId}/share`;

    return this.http.post(url, collaborators);
  }

  public createMapLink(mapUId: string): Observable<MapLink> {
    const url = `${apiBaseUrl}/v1/meta/maps/${mapUId}/map-link`;

    return this.http.post(url);
  }

  public getMapLinks(params: QueryParams): Observable<QueryResult<MapLink>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/meta/maps/map-links?${queryString}`;

    return this.http.get<QueryResult<MapLink>>(url);
  }

  public deleteMapLink(mapLinkUId: string) {
    const url = `${apiBaseUrl}/v1/meta/maps/map-link/${mapLinkUId}`;

    return this.http.delete(url);
  }

  public getMapLayerView(): Observable<MapLayerView[]> {
    const url = `${apiBaseUrl}/v1/meta/layers/view`;

    return this.http.get<MapLayerView[]>(url);
  }

  public getDatasetsExportTemplates(datasetUIds: string[], params?: QueryParams): Observable<QueryResult<ExportTemplate>> {
    const queryString = this.http.queryStringFromObject({
      ...params
    });

    const url = `${apiBaseUrl}/v1/analytics/templates/query?${queryString}`;

    const request = {
      datasetUIds: datasetUIds
    };

    return this.http.post<QueryResult<ExportTemplate>>(url, request);
  }

  public generateDocumentFromTemplate(template: ExportTemplate, documents: any[], options: number): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/templates/generate-document`;

    const request = {
      template: template,
      documents: documents,
      options: options
    };

    return this.http.post<any>(url, request, { responseType: 'arraybuffer' });
  }

  public getDocumentsAndGenerateDocumentFromTemplate(template: ExportTemplate, queryMatchRuleDto: any, options: number): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/templates/get-data-generate-document`;

    const request = {
      template: template,
      queryMatchRuleDto: queryMatchRuleDto,
      options: options
    };

    return this.http.post<any>(url, request, {responseType: "arraybuffer"});
  }

  public getTimezoneFromLocation(lat: number, lng: number): Observable<any> {
    const url = `${apiBaseUrl}/v1/meta/maps/timezone-by-location?latitude=${lat}&longitude=${lng}`;

    return this.http.get<any>(url);
  }
}
